import { Fragment, useEffect, useRef, useState } from "react";
import { findSubNode, getCustomisationValue, getGroupData } from "../../EditablePreview";
import './FallingObjects.scss';
import { destroyPhysicsControllerSingleton, getPhysicsControllerSingleton, PhysicsController } from "../../../../../lib/physics";
import Matter from "matter-js";
import VisualisedNode from "../VisualisedNode";

// const timeouts = [];

const FallingObjects = (props) => {
    // console.log("FallingObjects props:", props);
    const node = props.node;

    const timeouts_ref = useRef([]);
    const timeouts = timeouts_ref.current;

    const [tick, setTick] = useState(0);
    const updateTick = () => {
        setTick(prev => prev + 1);
    }
    const [notePanelIsDisplayed, setNotePanelIsDisplayed] = useState(true);
    const hideNotePanel = (e) => {
        e.stopPropagation();
        setNotePanelIsDisplayed(false);
    }

    // const objectCount = getGroupData(props.node.customisationVars, props.objectsCustomisationLocation ?? 'visual.objects.object', -1).length;

    const [liveObjects, setLiveObjects] = useState([]);
    const objectCountRef = useRef(0);

    const physicsControllerRef = useRef(null);

    const initPhysics = () => {
        physicsControllerRef.current = new PhysicsController();
        const physicsController = physicsControllerRef.current; // getPhysicsControllerSingleton();

        // create some invisible walls
        physicsController.createRectangleObject(50, 2000, null, {density: 1, restitution: 0.6, friction: 0, frictionStatic: 0, isStatic: true, position: {x: -25, y: 540}});
        physicsController.createRectangleObject(50, 2000, null, {density: 1, restitution: 0.6, friction: 0, frictionStatic: 0, isStatic: true, position: {x: 1945, y: 540}});
        physicsController.createRectangleObject(2000, 50, null, {density: 1, restitution: 0.6, friction: 20, frictionStatic: 20, isStatic: true, position: {x: 1920 / 2, y: 1105}});
    }

    const spawnObject = () => {
        if (objectCountRef.current >= 20) {
            // Too many objects, wait and try again later
            const tid = setTimeout(() => {
                spawnObject();
                timeouts.splice(timeouts.indexOf(tid), 1);
            }, 1000);
            timeouts.push(tid);
            return;
        }
        // Do an object in the top 100 pixels? If so, no spawn this tick
        for (let i = 0; i < liveObjects.length; i++) {
            const liveObj = liveObjects[i];
            if (liveObj.physicsView.position.y < 100) {
                const tid = setTimeout(() => {
                    spawnObject();
                    timeouts.splice(timeouts.indexOf(tid), 1);
                }, 500);
                timeouts.push(tid);
                return;
            }
        }
        const objectsGroupData = getGroupData(props.node.customisationVars, props.objectsCustomisationLocation ?? 'visual.objects.object', -1);
        // console.log("FallingObjects: Spawning object. Objects data:", objectsGroupData, props.node.customisationVars);
        const objFrequency = [];
        for (let i = 0; i < objectsGroupData.length; i++) {
            for (let j = 0; j < (objectsGroupData[i].appearFrequency || 1); j++) {
                objFrequency.push(i);
            }
        }
        const newObject = {
            index: objFrequency[Math.floor(Math.random() * objFrequency.length)],
            x: Math.floor(Math.random() * 94) + 3, // between 3 and 97 %
            id: Date.now(),

            physicsView: {
                position: {x: 100 + Math.floor(Math.random() * 1720), y: -200},
                rotation: 0,
                destroy: () => {},
            },
            physicsObject: null,
        }
        setLiveObjects(prev => {
            const newArray = [...prev, newObject];
            objectCountRef.current = newArray.length;
            return newArray;
        });
        const tid1 = setTimeout(() => {
            const liveObject = newObject;
            if (liveObject && liveObject.physicsObject) {
                // Time to remove the object from physics simulation
                const physicsController = physicsControllerRef.current;
                if (physicsController) {
                    physicsController.destroyObject(liveObject.physicsObject);
                }
            }
            setLiveObjects(prev => {
                const newArray = prev.filter(obj => obj.id !== newObject.id);
                objectCountRef.current = newArray.length;
                return newArray;
            });
            timeouts.splice(timeouts.indexOf(tid1), 1);
        }, 10000);
        const tid2 = setTimeout(() => {
            spawnObject();
            timeouts.splice(timeouts.indexOf(tid2), 1);
        }, 300 + Math.floor(Math.random() * 600));

        timeouts.push(tid1);
        timeouts.push(tid2);
    }

    useEffect(() => {
        initPhysics();
        spawnObject();
        const physicsUpdateInterval = setInterval(() => {
            const physicsController = physicsControllerRef.current;
            physicsController.update(2);
            updateTick();
        }, 1000 / 30); // 30 FPS
        return () => {
            while (timeouts.length > 0) {
                clearTimeout(timeouts.pop());
            }
            clearInterval(physicsUpdateInterval);
            // destroyPhysicsControllerSingleton();
            physicsControllerRef.current?.destroy();
            physicsControllerRef.current = null;
        };
    }, []);

    useEffect(() => {
        // If object count changes, reset live objects
        setLiveObjects([]);
    }, [node]);

    return (
        <>
            {liveObjects.map((obj) => {
                const objectsGroupData = getGroupData(props.node.customisationVars, props.objectsCustomisationLocation ?? 'visual.objects.object', -1);
                const objectData = objectsGroupData[obj.index];
                if (!objectData || !objectData.filename || !objectData.filename.srcUrl) {
                    return null;
                }
                const objScale = (objectData?.imageAutoSize ?? true) ? 1 : objectData.imageScale / 100;
                const defaultsData = getGroupData(props.node.customisationVars, 'visual.objects.defaults', 0) || {};
                const physicsScale = objectData.collisonAutoSize ? (defaultsData.physicsScale || 100) / 100 : (objectData.physicsScale || 100) / 100;
                const collisionWidth = (obj.width || 0) * physicsScale * objScale * props.previewScale;
                const collisionHeight = objectData.shape === 'rectangle' ? (obj.height || 0) * physicsScale * objScale * props.previewScale : collisionWidth;
                const scaledObjHeight = (obj.height || 0) * objScale * props.previewScale;
                const scaledObjWidth = (obj.width || 0) * objScale * props.previewScale;
                const oldObj = obj.collisionShape && (collisionWidth !== obj.collisionWidth || collisionHeight !== obj.collisionHeight || obj.collisionShape !== objectData.shape);
                
                const tagObject = props.node.customisationVars?.visual?.objects?.defaults?.[0]?.tagObjects === true;
                const tagSettings = props.node.customisationVars?.visual?.objects?.defaults?.[0];
                const tagSettingsNode = findSubNode(props.node.parent, 'Object Tagging');
                const tagStyle = {};
                if (tagObject && tagSettingsNode) {
                    const tagPos = props.node.customisationVars?.visual?.objects?.defaults?.[0]?.tagPosition ?? 'bottom';
                    tagStyle.left = '50%';
                    tagStyle.position = 'absolute';
                    if (tagPos === 'top') {
                        tagStyle.top = `calc(50% - ${scaledObjHeight / 2}px)`;
                        tagStyle.transform = 'translate(-50%, 0)';
                    } else if (tagPos === 'bottom') {
                        tagStyle.top = `calc(50% + ${scaledObjHeight / 2}px)`;
                        tagStyle.transform = 'translate(-50%, -100%)';
                    } else if (tagPos === 'middle') {
                        tagStyle.top = '50%';
                        tagStyle.transform = 'translate(-50%, -50%)';
                    }
                    // tagStyle.transform += ` scale(${props.previewScale})`;
                    // tagStyle.transformOrigin = 'center';

                    tagStyle.fontFamily = tagSettings?.tagFont || 'Arial';
                    tagStyle.fontSize = `${(tagSettings?.tagFontSize || 14) * props.previewScale}px`;
                    tagStyle.color = tagSettings?.tagFontColour || '#FFFFFF';
                    tagStyle.fontWeight = tagSettings?.tagIsBold ? 'bold' : 'normal';
                }
                const tagStrokeStyle = {};
                if (tagSettings?.addTagOutline) {
                    const outlineThickness = Math.ceil((tagSettings?.tagOutlineThickness || 1) * props.previewScale);
                    /* tagStyle.textShadow = `${-outlineThickness} 0 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, ${outlineThickness} 0 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, 0 ${outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, 0 ${-outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, ${-outlineThickness} ${-outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, ${outlineThickness} ${-outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, ${outlineThickness} ${outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                    tagStyle.textShadow += `, ${-outlineThickness} ${outlineThickness} 0 ${tagSettings?.tagFontOutlineColour || '#000000'}`; */
                    tagStrokeStyle.webkitTextStroke = `${outlineThickness}px ${tagSettings?.tagFontOutlineColour || '#000000'}`;
                }
                

                return (
                    <Fragment key={obj.id}>
                        <div className="falling-zapper-object" style={{/*left: obj.x + '%'*/left: obj.physicsView.position.x * props.previewScale + 'px', top: obj.physicsView.position.y * props.previewScale + 'px', transform: `translate(-50%, -50%) rotate(${obj.physicsView.rotation}rad)`}}>
                            <div style={{position: 'relative'}}>
                                {!props.thumbnailMode && oldObj &&
                                    <div className={`zapper-collision-area old${obj.collisionShape === 'circle' ? ' circle' : ''}`} style={{
                                        width: obj.collisionWidth + 'px',
                                        height: obj.collisionHeight + 'px',
                                        bottom:  `${(obj.scaledObjHeight - obj.collisionHeight) / 2}px`,
                                    }} />
                                }
                                <img className="zapper-inner-object" src={objectData.filename.srcUrl} alt="Falling Object" style={{transform: `scale(${props.previewScale}) scale(${objScale})`, opacity: oldObj ? 0.6 : 1, filter: oldObj ? 'grayscale(50%)' : 'none'}} 
                                    onLoad={
                                        (e) => {
                                            // set obj.width / obj.height based on image size
                                            const imgWidth = e.target.naturalWidth;
                                            const imgHeight = e.target.naturalHeight;
                                            obj.width = imgWidth;
                                            obj.height = imgHeight;

                                            // Create physics object - we do this in onLoad to ensure we have the image dimensions
                                            if (obj.physicsObject === null) {
                                                const physicsController = physicsControllerRef.current;
                                                if (physicsController) {
                                                    const objScale = (objectData?.imageAutoSize ?? true) ? 1 : objectData.imageScale / 100;
                                                    const defaultsData = getGroupData(props.node.customisationVars, 'visual.objects.defaults', 0) || {};
                                                    const physicsScale = objectData.collisonAutoSize ? (defaultsData.physicsScale || 100) / 100 : (objectData.physicsScale || 100) / 100;
                                                    const collisionWidth = (obj.width || 0) * physicsScale * objScale; // * props.previewScale;
                                                    const collisionHeight = objectData.shape === 'rectangle' ? (obj.height || 0) * physicsScale * objScale /* props.previewScale*/ : collisionWidth;
                                                    // const scaledObjHeight = (obj.height || 0) * objScale * props.previewScale;

                                                    obj.collisionWidth = collisionWidth * props.previewScale;
                                                    obj.collisionHeight = collisionHeight * props.previewScale;
                                                    obj.collisionShape = objectData.shape;
                                                    obj.scaledObjHeight = (obj.height || 0) * objScale * props.previewScale;

                                                    const physicConfig = {
                                                        density: 0.1,
                                                        restitution: 0.6,
                                                        friction: 1,
                                                        frictionAir: 0,
                                                        frictionStatic: 20,
                                                        ignoreRotation: false,
                                                        position: {x: obj.physicsView.position.x, y: obj.physicsView.position.y},
                                                    }

                                                    if (objectData.shape === 'rectangle') {
                                                        obj.physicsObject = physicsController.createRectangleObject(
                                                            collisionWidth, 
                                                            collisionHeight, 
                                                            obj.physicsView,
                                                            physicConfig,
                                                            false
                                                        );
                                                    } else {
                                                        obj.physicsObject = physicsController.createCircleObject(
                                                            collisionWidth / 2, 
                                                            obj.physicsView,
                                                            physicConfig,
                                                            false
                                                        );
                                                    }

                                                    Matter.Body.rotate(obj.physicsObject.physicsBody, Math.random() * Math.PI * 2);
                                                    let startSpin = Math.random() * (0.2 - -0.2) + -0.2;
                                                    Matter.Body.setAngularVelocity(obj.physicsObject.physicsBody, startSpin);
                                                    let startXVel = Math.random() * (3 - -3) + -3;
                                                    Matter.Body.setVelocity(obj.physicsObject.physicsBody, {x: startXVel, y: 0});
                                                }
                                            }
                                        }
                                    } 
                                    onClick={
                                        (e) => {
                                            props.activateNodeFunction(node.subNodes[obj.index]);
                                            e.stopPropagation();
                                        }
                                    }
                                />
                                {!props.thumbnailMode && collisionWidth > 0 && collisionHeight > 0 &&
                                    <div className={`zapper-collision-area${objectData.shape === 'circle' ? ' circle' : ''}`} style={{
                                        width: collisionWidth + 'px',
                                        height: collisionHeight + 'px',
                                        bottom:  `${(scaledObjHeight - collisionHeight) / 2}px`,
                                    }} 
                                    onClick={
                                        (e) => {
                                            props.activateNodeFunction(node.subNodes[obj.index]);
                                            e.stopPropagation();
                                        }
                                    }
                                    />
                                }
                            </div>
                        </div>
                        {tagObject && tagSettingsNode &&
                            <div className="falling-zapper-object" style={{/*left: obj.x + '%'*/left: obj.physicsView.position.x * props.previewScale + 'px', top: obj.physicsView.position.y * props.previewScale + 'px', transform: `translate(-50%, -50%)`}}>
                                <div style={{position: 'relative'}}>
                                    <div style={{position: 'relative'}}>
                                        <div style={{...tagStyle, ...tagStrokeStyle}}>
                                            [username_tag]
                                        </div>
                                    </div>
                                    <div style={tagStyle}>
                                        <VisualisedNode
                                            {...props}
                                            previewScale={1}
                                            applyScale={1}
                                            node={tagSettingsNode}
                                            dontVisualiseModuleSettings={{typography: true}}
                                        >
                                            [username_tag]
                                        </VisualisedNode>
                                    </div>
                                </div>
                                
                            </div>
                        }
                    </Fragment>
                )
            })}
            {!props.thumbnailMode && notePanelIsDisplayed &&
                <div className="v2-visual-sim-note">
                    <div className="rel-inner">
                        <p>Note this simulation may not accurately represent the final in-stream behaviour as the number of objects and drop side bias can be adjusted at runtime.
                        <br />
                        Also note, changes to physics settings are not reflected immediately, you may need to wait for existing objects to clear.</p>
                        <div className="v2-visual-sim-note-close-btn" onClick={hideNotePanel}>X</div>
                    </div>
                </div>
            }
        </>
    );

}

export default FallingObjects;
